import { useAuth0 } from '@auth0/auth0-react';

/**
 * Local development bypass for Auth0.
 *
 * Only on when both are true:
 *   - the dev server is running (`react-scripts build` sets NODE_ENV to 'production');
 *   - REACT_APP_DISABLE_AUTH=true is set in .env.development.local, which is gitignored.
 *
 * So a production build cannot turn the bypass on, not even when the environment variable
 * gets set there by accident.
 */
export const authDisabled =
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_DISABLE_AUTH === 'true';

const devUser = {
    name: 'Lokale ontwikkelaar',
    nickname: 'local-dev',
    email: 'dev@localhost',
};

/*
    These fake functions deliberately live outside the hook.

    Inside, they would get a new identity on every render, and anything that puts them in a
    dependency array - the axios instance, the SignalR connection - would rebuild itself
    endlessly. That exact difference between local and published costs an afternoon of
    hunting.
*/
const devAccessToken = async () => 'local-dev-token';
const devLoginWithRedirect = async () => undefined;
const devLogout = () => undefined;

/**
 * Drop-in replacement for useAuth0(). Normally returns the real Auth0 context; with the
 * bypass on it pretends you are signed in.
 */
export const useAuth = (): ReturnType<typeof useAuth0> => {
    const auth0 = useAuth0();

    if (!authDisabled) {
        return auth0;
    }

    return {
        ...auth0,
        isLoading: false,
        isAuthenticated: true,
        error: undefined,
        user: devUser,
        loginWithRedirect: devLoginWithRedirect,
        logout: devLogout,
        getAccessTokenSilently:
            devAccessToken as typeof auth0.getAccessTokenSilently,
        // Mocked as well, so the consent banner can never open a real Auth0 popup on a
        // developer who signed in to nothing.
        getAccessTokenWithPopup:
            devAccessToken as typeof auth0.getAccessTokenWithPopup,
    };
};
